import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Flow,
  FlowReadiness,
  FlowState,
  FlowStep,
  FlowVersion,
} from "@zworks/console-contracts";
import * as React from "react";
import { Link } from "react-router";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ApiError,
  activateFlow,
  createFlow,
  flowVersionsQuery,
  flowsQuery,
  publishFlowVersion,
} from "@/lib/api";
import { diffSteps } from "@/lib/flow-diff";
import { useCurrentProject } from "@/lib/project";
import { formatDate } from "@/lib/format";
import { workflowTemplates } from "@/lib/workflow-templates";

const stateLabels: Record<FlowState, string> = {
  draft: "Draft",
  active: "Active",
  archived: "Archived",
};

const stateVariants: Record<FlowState, "secondary" | "success" | "outline"> = {
  draft: "secondary",
  active: "success",
  archived: "outline",
};

/** A labelled flow-state badge. Colour never carries the meaning alone. */
export function FlowStateBadge({ state }: { state: FlowState }) {
  return <Badge variant={stateVariants[state]}>{stateLabels[state]}</Badge>;
}

/** A labelled readiness badge. Not-ready is dashed: an unproven flow never looks approved. */
export function FlowReadinessBadge({ readiness }: { readiness: FlowReadiness }) {
  return readiness === "ready" ? (
    <Badge variant="success">Ready</Badge>
  ) : (
    <Badge variant="unknown">Not ready</Badge>
  );
}

/** The last run cell: the short id, or plain text when the flow never started one. */
function lastRunCell(flow: Flow) {
  if (!flow.lastRunId) return <span className="text-muted-foreground">Never</span>;
  return <span className="font-mono text-[0.8125rem]">{flow.lastRunId.slice(0, 8)}</span>;
}

/** A stable row key for a step row: name, kind, and whether it is a gate. */
function stepRowKey(step: FlowStep, index: number): string {
  return `${step.name}|||${step.kind}|||${step.gate ? "gate" : "step"}|||${index}`;
}

/**
 * The version history of one workflow, as the Studio board draws it: every version oldest first
 * with who is pinned by runs, and a diff between any two selected versions. Publishing
 * inserts a new row and never changes a pinned one.
 */
function VersionHistory({ projectId, flow }: { projectId: string; flow: Flow }) {
  const versions = useQuery({
    ...flowVersionsQuery(projectId, flow.id),
    enabled: Boolean(projectId) && Boolean(flow.id),
  });
  const queryClient = useQueryClient();
  const [from, setFrom] = React.useState<number | null>(null);
  const [to, setTo] = React.useState<number | null>(null);
  const [activateError, setActivateError] = React.useState<string | null>(null);

  const publish = useMutation({
    mutationFn: (steps: FlowStep[]) => publishFlowVersion(projectId, flow.id, { steps }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "flows"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "flows", flow.id, "versions"],
      });
    },
  });

  const activation = useMutation({
    mutationFn: () => activateFlow(projectId, flow.id),
    onSuccess: () => {
      setActivateError(null);
      void queryClient.invalidateQueries({ queryKey: ["projects", projectId, "flows"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        setActivateError(error.problem?.detail ?? error.message);
      } else {
        setActivateError("The workflow could not be activated.");
      }
    },
  });

  if (versions.isPending) return <LoadingState label="Loading versions" rows={3} />;
  if (versions.isError) {
    const error = versions.error;
    return (
      <ErrorState
        title="Versions could not be loaded"
        description={
          error instanceof ApiError
            ? (error.problem?.detail ?? error.message)
            : "The request could not be completed."
        }
        requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
        onRetry={() => void versions.refetch()}
      />
    );
  }
  const rows = versions.data;
  if (!rows) return <LoadingState label="Loading versions" rows={3} />;

  const current = rows[rows.length - 1] ?? null;
  const fromVersion = rows.find((v) => v.number === from) ?? null;
  const toVersion = rows.find((v) => v.number === to) ?? rows[rows.length - 1] ?? null;
  const diff = fromVersion && toVersion ? diffSteps(fromVersion.steps, toVersion.steps) : [];

  return (
    <div className="flex flex-col gap-4">
      {flow.readiness === "not-ready" ? (
        <div
          role="alert"
          className="rounded-lg border border-warning/40 bg-warning/6 p-3 text-[0.8125rem]"
        >
          <p className="font-medium text-foreground">This workflow cannot be activated yet</p>
          <p className="mt-0.5 text-muted-foreground">
            {flow.gateCount === 0 && flow.stepCount > 0
              ? "It deploys without a gate. Add a gate before activating it."
              : "Its current version holds no steps. Publish a version with steps first."}
          </p>
        </div>
      ) : null}
      {activateError ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/35 bg-danger/6 p-3 text-[0.8125rem]"
        >
          <p className="font-medium text-foreground">The workflow could not be activated</p>
          <p className="mt-0.5 text-muted-foreground">{activateError}</p>
        </div>
      ) : null}
      {publish.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/35 bg-danger/6 p-3 text-[0.8125rem]"
        >
          <p className="font-medium text-foreground">The version could not be published</p>
          <p className="mt-0.5 text-muted-foreground">
            {publish.error instanceof ApiError
              ? (publish.error.problem?.detail ?? publish.error.message)
              : "The request could not be completed."}
          </p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <Link to={`/flows/${flow.id}/edit`}>Edit draft</Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={flow.state !== "draft" || flow.readiness !== "ready" || activation.isPending}
          onClick={() => activation.mutate()}
        >
          {activation.isPending ? "Activating" : "Activate"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={publish.isPending || !current}
          onClick={() => {
            if (current) publish.mutate(current.steps);
          }}
        >
          {publish.isPending ? "Publishing" : "Publish new version"}
        </Button>
        <p className="text-xs text-muted-foreground">
          Publishing inserts a new version. A version a run is pinned to never changes.
        </p>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No versions yet"
          description="Publish the first version to start the history."
        />
      ) : (
        <ol className="flex flex-col gap-2">
          {rows.map((version: FlowVersion) => (
            <li
              key={version.id}
              className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm"
            >
              <span className="font-medium tabular-nums">v{version.number}</span>
              <FlowReadinessBadge readiness={version.readiness} />
              <span className="text-xs text-muted-foreground tabular-nums">
                {version.steps.length} {version.steps.length === 1 ? "step" : "steps"}
              </span>
              <span className="ml-auto flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={from === version.number}
                  onClick={() => setFrom(version.number)}
                >
                  Compare from
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={to === version.number}
                  onClick={() => setTo(version.number)}
                >
                  Compare to
                </Button>
              </span>
            </li>
          ))}
        </ol>
      )}

      {fromVersion && toVersion ? (
        <section
          aria-label={`Diff of version ${fromVersion.number} to version ${toVersion.number}`}
        >
          <h3 className="text-sm font-medium">
            v{fromVersion.number} to v{toVersion.number}
          </h3>
          <div className="mt-2 overflow-x-auto rounded-lg border">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">
                Steps added, removed, or unchanged between the two versions
              </caption>
              <thead>
                <tr>
                  {["Step", "Kind", "Gate", "Change"].map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {diff.map((row, index) => (
                  <tr key={stepRowKey(row.step, index)} className="hover:bg-muted/50">
                    <td className="border-b px-3 py-2 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {row.step.name}
                    </td>
                    <td className="border-b px-3 py-2 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem]">
                      {row.step.kind}
                    </td>
                    <td className="border-b px-3 py-2 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {row.step.gate ? "Gate" : "–"}
                    </td>
                    <td className="border-b px-3 py-2 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <Badge
                        variant={
                          row.change === "added"
                            ? "success"
                            : row.change === "removed"
                              ? "danger"
                              : "outline"
                        }
                      >
                        {row.change === "added"
                          ? "Added"
                          : row.change === "removed"
                            ? "Removed"
                            : "Unchanged"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}

/**
 * The Studio workflows tab, as the board draws it: one table with state, version, steps, gates,
 * readiness, and the last run, plus the version history with a diff between two versions.
 * Dates read DD/MM/YYYY. Status is always a labelled badge.
 */
export function FlowsPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project, loaded } = useCurrentProject();
  const [openFlow, setOpenFlow] = React.useState<Flow | null>(null);
  const [name, setName] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const queryClient = useQueryClient();
  const list = useQuery({
    ...flowsQuery(project?.id ?? ""),
    enabled: loaded && project !== null,
  });

  const creation = useMutation({
    mutationFn: (next: string) => createFlow(project?.id ?? "", { name: next }),
    onSuccess: () => {
      setName("");
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ["projects", project?.id, "flows"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        setFormError(error.problem?.detail ?? error.message);
      } else {
        setFormError("The workflow could not be created.");
      }
    },
  });

  const [templateError, setTemplateError] = React.useState<string | null>(null);

  /** Starts a workflow from a template: creates the draft, then publishes its first version. */
  const fromTemplate = useMutation({
    mutationFn: async (templateId: string) => {
      const template = workflowTemplates.find((candidate) => candidate.id === templateId);
      if (!template) throw new Error("Unknown template.");
      const created = await createFlow(project?.id ?? "", { name: template.name });
      await publishFlowVersion(project?.id ?? "", created.id, { steps: template.steps });
      return created;
    },
    onSuccess: () => {
      setTemplateError(null);
      void queryClient.invalidateQueries({ queryKey: ["projects", project?.id, "flows"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        setTemplateError(error.problem?.detail ?? error.message);
      } else {
        setTemplateError("The workflow could not be created from the template.");
      }
    },
  });

  // A disabled query stays pending, so the empty project is decided before the query.
  if (!loaded || (project !== null && list.isPending)) {
    return (
      <>
        <PageHeader title="Workflows" headingLevel={headingLevel} />
        <LoadingState label="Loading workflows" rows={6} />
      </>
    );
  }

  if (project === null) {
    return (
      <>
        <PageHeader title="Workflows" headingLevel={headingLevel} />
        <EmptyState
          title="No projects yet"
          description="Create your first project. Its workflows appear here."
        />
      </>
    );
  }

  if (list.isError) {
    const error = list.error;
    return (
      <>
        <PageHeader title="Workflows" headingLevel={headingLevel} />
        {error instanceof ApiError && error.status === 403 ? (
          <ForbiddenState
            title="You do not have access to this project"
            description="Ask an owner to invite you. Nothing from the project is shown."
          />
        ) : (
          <ErrorState
            title="Workflows could not be loaded"
            description={
              error instanceof ApiError
                ? (error.problem?.detail ?? error.message)
                : "The request could not be completed."
            }
            requestId={error instanceof ApiError ? (error.requestId ?? "unknown") : "unknown"}
            onRetry={() => void list.refetch()}
          />
        )}
      </>
    );
  }

  // The project switch can disable the query mid-flight, so data is read once it exists.
  const data = list.data;
  if (!data) {
    return (
      <>
        <PageHeader title="Workflows" headingLevel={headingLevel} />
        <LoadingState label="Loading workflows" rows={6} />
      </>
    );
  }

  const published = data.filter((flow) => flow.state === "active").length;
  const drafts = data.filter((flow) => flow.state === "draft").length;

  return (
    <>
      <PageHeader title="Workflows" headingLevel={headingLevel} />

      <dl className="grid gap-3 sm:grid-cols-3">
        {[
          { label: "Workflows", value: String(data.length) },
          { label: "Published", value: String(published) },
          { label: "Drafts", value: String(drafts) },
        ].map((card) => (
          <div key={card.label} className="rounded-lg border bg-card px-4 py-3">
            <dt className="text-xs font-medium text-muted-foreground">{card.label}</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums">{card.value}</dd>
          </div>
        ))}
      </dl>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) {
            setFormError("A name is needed to create a workflow.");
            return;
          }
          creation.mutate(trimmed);
        }}
      >
        <div className="flex min-w-60 flex-col gap-2">
          <label htmlFor="new-flow-name" className="text-[0.8125rem] font-medium">
            New workflow
          </label>
          <Input
            id="new-flow-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="refund-pipeline"
            maxLength={120}
          />
        </div>
        <Button type="submit" disabled={creation.isPending}>
          {creation.isPending ? "Creating" : "New workflow"}
        </Button>
        {formError ? (
          <p role="alert" className="mt-2 w-full text-[0.8125rem] text-danger">
            {formError}
          </p>
        ) : null}
      </form>

      <section aria-label="Start from a template" className="flex flex-col gap-3">
        <h2 className="text-sm font-medium">Start from a template</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {workflowTemplates.map((template) => (
            <Card key={template.id} className="flex flex-col gap-2 p-4">
              <p className="text-[0.8125rem] font-medium">{template.name}</p>
              <p className="text-[0.8125rem] text-muted-foreground">
                {template.summary} {template.steps.length}{" "}
                {template.steps.length === 1 ? "step" : "steps"}.
              </p>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={fromTemplate.isPending}
                  onClick={() => fromTemplate.mutate(template.id)}
                >
                  {fromTemplate.isPending ? "Creating" : `Use ${template.name}`}
                </Button>
              </div>
            </Card>
          ))}
        </div>
        {templateError ? (
          <p role="alert" className="text-[0.8125rem] text-danger">
            {templateError}
          </p>
        ) : null}
      </section>

      {data.length === 0 ? (
        <EmptyState
          title="No workflows yet"
          description="Create the first workflow. It starts as a draft with an empty first version."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">
                Workflows, with state, version, readiness, and last run
              </caption>
              <thead>
                <tr>
                  {[
                    "Workflow",
                    "State",
                    "Version",
                    "Steps",
                    "Gates",
                    "Readiness",
                    "Last run",
                    "Updated",
                  ].map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((flow: Flow) => (
                  <tr key={flow.id} className="group hover:bg-muted/50">
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <button
                        type="button"
                        className="font-medium underline-offset-4 hover:underline"
                        onClick={() => setOpenFlow(flow)}
                      >
                        {flow.name}
                      </button>
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <FlowStateBadge state={flow.state} />
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
                      v{flow.currentVersion}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
                      {flow.stepCount}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
                      {flow.gateCount}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <FlowReadinessBadge readiness={flow.readiness} />
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {lastRunCell(flow)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
                      {formatDate(flow.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={openFlow !== null} onOpenChange={(open) => !open && setOpenFlow(null)}>
        <DialogContent aria-label="Flow versions">
          {openFlow ? (
            <>
              <DialogTitle>
                {openFlow.name}{" "}
                <span className="text-muted-foreground">v{openFlow.currentVersion}</span>
              </DialogTitle>
              <VersionHistory projectId={project.id} flow={openFlow} />
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
